import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, Clock, X } from "lucide-react";

const TrialBanner = () => {
  const { subscription, user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  // Don't show for staff members
  if (user?.role === "staff") {
    return null;
  }

  // Don't show if not on trial or dismissed
  if (!subscription?.is_trial || dismissed) {
    return null;
  }

  const daysRemaining = subscription.trial_days_remaining;

  // Determine urgency level
  const isUrgent = daysRemaining !== null && daysRemaining <= 3;
  const isWarning =
    daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 3;

  // Background colors based on urgency
  const bgColor = isUrgent
    ? "bg-gradient-to-r from-red-500/90 to-orange-500/90"
    : isWarning
      ? "bg-gradient-to-r from-amber-500/90 to-yellow-500/90"
      : "bg-gradient-to-r from-purple-500/90 to-indigo-500/90";

  // Full message for sm+ screens
  const getFullMessage = () => {
    if (daysRemaining === null)
      return "You're on a free trial. Upgrade to continue using SlotBook.";
    if (daysRemaining === 0)
      return "Your trial ends today! Upgrade now to keep your account active.";
    if (daysRemaining === 1)
      return "Your trial ends tomorrow! Upgrade now to avoid interruption.";
    return `Your trial will end in ${daysRemaining} days. Upgrade to continue using SlotBook.`;
  };

  // Short message for mobile — always fits on one line
  const getShortMessage = () => {
    if (daysRemaining === null) return "You're on a free trial.";
    if (daysRemaining === 0) return "Your trial ends today!";
    if (daysRemaining === 1) return "Your trial ends tomorrow!";
    return `Your trial will end in ${daysRemaining} days.`;
  };

  return (
    <div
      className={`${bgColor} text-white px-3 py-2 sm:px-4 sm:py-3 shadow-lg`}
      data-testid="trial-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 sm:gap-4">
        {/* Message — short on mobile, full on sm+ */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {isUrgent ? (
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 animate-pulse" />
          ) : (
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          )}
          <p className="text-xs sm:text-sm font-medium truncate">
            <span className="sm:hidden">{getShortMessage()}</span>
            <span className="hidden sm:inline">{getFullMessage()}</span>
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href="/dashboard/pricing"
            className="text-xs font-semibold bg-white/20 hover:bg-white/30 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full transition-colors whitespace-nowrap"
            data-testid="trial-upgrade-btn"
          >
            <span className="sm:hidden">Upgrade</span>
            <span className="hidden sm:inline">Upgrade Now</span>
          </a>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
